import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { sportsMenuFullQuery } from '@/api/queries';
import { translateSportsLabel } from '@/i18n/sports-label';
import { resolveMenuPath } from '@/shell/sports-menu-utils';
import { EmptyState } from '@/ui';
import { EventsFeed, EventsFeedSkeleton } from './-components/EventsFeed';
import { TournamentOfferTabs } from './-components/TournamentOfferTabs';
import { useTournamentSeasons } from './-components/useTournamentSeasons';

export const Route = createFileRoute('/sports/$sport/$category/$tournament/')({
  component: TournamentEventsPage,
});

function TournamentEventsPage() {
  const { sport, category, tournament } = Route.useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const menuQuery = useQuery(sportsMenuFullQuery());

  const resolved = useMemo(
    () => resolveMenuPath(menuQuery.data, sport, category, tournament),
    [menuQuery.data, sport, category, tournament],
  );

  const filter = useMemo(
    () =>
      resolved
        ? {
            Sport: resolved.sport.Name,
            Categories: [resolved.category.Name],
            Tournaments: [resolved.tournament.Name],
          }
        : null,
    [resolved],
  );

  const seasons = useTournamentSeasons(
    resolved
      ? {
          Sport: resolved.sport.Name,
          Category: resolved.category.Name,
          Tournament: resolved.tournament.Name,
        }
      : null,
  );

  if (menuQuery.isPending) {
    return <EventsFeedSkeleton />;
  }

  if (!resolved || !filter) {
    return (
      <EmptyState
        icon="topleagues"
        title={t('TournamentNotFound', { defaultValue: 'Tournament not found' })}
        description={t('TournamentNotInMenu', {
          defaultValue: 'This tournament is not in the current sports menu.',
        })}
      />
    );
  }

  const sportLabel = translateSportsLabel(i18n, t, resolved.sport.Id, resolved.sport.Name);
  const categoryLabel = translateSportsLabel(i18n, t, resolved.category.Id, resolved.category.Name);
  const tournamentLabel = translateSportsLabel(
    i18n,
    t,
    resolved.tournament.Id,
    resolved.tournament.Name,
  );

  return (
    <EventsFeed
      filter={filter}
      title={tournamentLabel}
      subtitle={`${categoryLabel} · ${sportLabel}`}
      belowTitle={
        seasons.showTabs ? (
          <TournamentOfferTabs
            value="events"
            sport={sport}
            category={category}
            tournament={tournament}
          />
        ) : null
      }
      emptyDescription={t('NoEventsForTournament', {
        defaultValue: 'No events available for this tournament right now.',
      })}
      onOpenEvent={({ eventId, offer }) => {
        void navigate({
          to: '/sports/$sport/$category/$tournament/$eventId',
          params: {
            sport,
            category,
            tournament,
            eventId: String(eventId),
          },
          search: { offer },
        });
      }}
    />
  );
}
